import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'bootstrap';
import { ButtonFormModalProps } from '../../types/components';

/**
 * A reusable modal component for creating and editing buttons
 */
function ButtonFormModal({
  modalId = 'buttonModal',
  formData,
  isEditMode = false,
  popularColors,
  popularIcons,
  onFormChange,
  onSubmit,
  isLoggedIn = false
}: ButtonFormModalProps) {
  const { t } = useTranslation();
  const [colorHasOverflow, setColorHasOverflow] = useState(false);
  const [iconHasOverflow, setIconHasOverflow] = useState(false);
  const colorContainerRef = useRef<HTMLDivElement>(null);
  const iconContainerRef = useRef<HTMLDivElement>(null);

  // Check for overflow when component mounts or when the modal is shown
  useEffect(() => {
    const checkOverflow = () => {
      if (colorContainerRef.current) {
        setColorHasOverflow(
          colorContainerRef.current.scrollWidth > colorContainerRef.current.clientWidth
        );
      }
      
      if (iconContainerRef.current) {
        setIconHasOverflow(
          iconContainerRef.current.scrollWidth > iconContainerRef.current.clientWidth
        );
      }
    };

    // Check initially
    checkOverflow();
    
    // Also check when modal is shown
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', checkOverflow);
      
      // Cleanup
      return () => {
        modalElement.removeEventListener('shown.bs.modal', checkOverflow);
      };
    }
  }, [modalId]);

  // Handle costToClick change
  const handleCostToClickChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      // If input is empty, pass an empty string to the handler
      onFormChange('costToClick', '');
    } else {
      // Otherwise convert to number and pass
      onFormChange('costToClick', Number(value));
    }
  };

  return (
    <div className="modal fade" id={modalId} tabIndex={-1} aria-labelledby={`${modalId}Label`} aria-hidden="true">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id={`${modalId}Label`}>
              {isEditMode ? t('buttons.edit') : t('buttons.addNewButton')}
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              data-bs-dismiss="modal" 
              aria-label={t('common.close', 'Close')}
            ></button>
          </div>
          <form onSubmit={onSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">{t('buttons.buttonName')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => onFormChange('name', e.target.value)}
                  required
                />
                {!isEditMode && (
                  <div className="form-text small text-muted">
                    <i className="bi bi-info-circle-fill me-1" aria-hidden="true"></i>
                    {t('buttons.suggestionHint', 'A suggested name is provided, feel free to customize it')}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">{t('buttons.buttonColor')}</label>
                <div className="position-relative">
                  <div className="d-flex border rounded p-2 overflow-hidden" style={{ scrollbarWidth: 'none' }}>
                    <div 
                      ref={colorContainerRef}
                      className="d-flex gap-2 flex-nowrap overflow-x-auto" 
                      id="colorButtonsContainer">
                      {popularColors.map((color: string) => (
                        <button
                          key={color}
                          type="button"
                          className={`btn btn-sm ${formData.color === color ? 'btn-secondary' : 'btn-outline-secondary'} d-flex align-items-center justify-content-center selection-button flex-shrink-0`}
                          onClick={() => onFormChange('color', color)}
                        >
                          <div
                            className="rounded selection-button-content"
                            style={{ backgroundColor: color }}
                          ></div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {colorHasOverflow && (
                    <div className="d-flex justify-content-center align-items-center mt-1">
                      <div className="d-flex gap-1">
                        <i className="bi bi-chevron-left text-muted small"></i>
                        <div className="text-muted small">
                          <i className="bi bi-arrow-left-right"></i>
                          <span className="ms-1 d-none d-sm-inline">{t('common.scroll', 'Scroll')}</span>
                        </div>
                        <i className="bi bi-chevron-right text-muted small"></i>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">{t('buttons.buttonIcon')}</label>
                <div className="position-relative">
                  <div className="d-flex border rounded p-2 overflow-hidden" style={{ scrollbarWidth: 'none' }}>
                    <div 
                      ref={iconContainerRef}
                      className="d-flex gap-2 flex-nowrap overflow-x-auto" 
                      id="iconButtonsContainer">
                      {popularIcons.map((icon: string) => (
                        <button
                          key={icon}
                          type="button"
                          className={`btn btn-sm ${formData.icon === icon ? 'btn-secondary' : 'btn-outline-secondary'} d-flex align-items-center justify-content-center selection-button flex-shrink-0`}
                          onClick={() => onFormChange('icon', icon)}
                        >
                          <i className={`${icon} selection-button-icon`}></i>
                        </button>
                      ))}
                    </div>
                  </div>
                  {iconHasOverflow && (
                    <div className="d-flex justify-content-center align-items-center mt-1">
                      <div className="d-flex gap-1">
                        <i className="bi bi-chevron-left text-muted small"></i>
                        <div className="text-muted small">
                          <i className="bi bi-arrow-left-right"></i>
                          <span className="ms-1 d-none d-sm-inline">{t('common.scroll', 'Scroll')}</span>
                        </div>
                        <i className="bi bi-chevron-right text-muted small"></i>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Cost to click field */}
              <div className="mb-3">
                <label className="form-label">{t('buttons.costToClick')}</label>
                {isLoggedIn ? (
                  <>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-coin text-warning"></i>
                      </span>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.costToClick === undefined || formData.costToClick === null ? '' : formData.costToClick}
                        onChange={handleCostToClickChange}
                        min="0"
                        placeholder="0"
                      />
                    </div>
                    <div className="form-text small text-muted">
                      <i className="bi bi-info-circle-fill me-1" aria-hidden="true"></i>
                      {t('buttons.costToClickHint', 'Optional. Set a token cost required to tap this button.')}
                    </div>
                  </>
                ) : (
                  <div className="alert alert-light text-center border">
                    <div className="mb-3 text-warning">
                      <i className="bi bi-person-lock fs-1"></i>
                    </div>
                    <p>{t('buttons.costFeatureLoginRequired', 'Buttons with Cost to Tap are only available to logged-in users')}</p>
                    <div className="d-grid gap-2 col-md-8 mx-auto mt-3">
                      <a 
                        href="/login" 
                        className="btn btn-primary"
                        onClick={() => {
                          localStorage.setItem('returnPath', window.location.pathname);
                        }}
                      >
                        <i className="bi bi-box-arrow-in-right me-1"></i>
                        {t('auth.login', 'Login')}
                      </a>
                      <a 
                        href="/signup" 
                        className="btn btn-success"
                        onClick={() => {
                          localStorage.setItem('returnPath', window.location.pathname);
                        }}
                      >
                        <i className="bi bi-person-plus me-1"></i>
                        {t('auth.signup', 'Sign Up')}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                data-bs-dismiss="modal"
              >
                {t('common.cancel')}
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
              >
                {isEditMode ? t('common.save') : t('common.add')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ButtonFormModal; 